/**
 * OsInventory — OS-INV-16
 *
 * OS-specific inventory management for /os/inventory (replaces the shared
 * Made.bb Inventory component for the Offshore Sails tenant).
 *
 * Features (OS-INV-16):
 *   1. Default-location management panel — GET/PUT /api/os/settings/default-location
 *   2. Materials table — stock_storeroom, stock_working, and total columns
 *   3. Add/Edit dialogs — location picker for stock mutations
 *   4. OsPinAdmin panel — unchanged from OS-INV-14
 *
 * API:
 *   GET  /api/os/materials
 *   POST /api/os/materials   (body includes `location` for initial-stock routing)
 *   PUT  /api/os/materials/:id
 *   DELETE /api/os/materials/:id
 *   GET  /api/os/settings/default-location
 *   PUT  /api/os/settings/default-location
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../madebb/utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';
import { OsPinAdmin } from './OsPinAdmin';

// ── Types ──────────────────────────────────────────────────────────────────

type LocationKey = 'working' | 'storeroom';
type DefaultLocation = LocationKey | null;

/**
 * OS-INV-19 — Audit classification for a material.
 * A = monthly (30d), B = quarterly (90d), C = semi-annual (180d).
 * null = unclassified; treated as C by the scheduling engine.
 * Staff-auth view only — never shown in PIN-session context.
 */
type AuditClass = 'A' | 'B' | 'C';

/** OS-INV-19 — Display config for each audit class. */
const AUDIT_CLASS_META: Record<AuditClass, { label: string; color: string; tooltip: string }> = {
  A: { label: 'A', color: '#c62828', tooltip: 'Class A — Monthly (30 days)' },
  B: { label: 'B', color: '#e65100', tooltip: 'Class B — Quarterly (90 days)' },
  C: { label: 'C', color: '#1565c0', tooltip: 'Class C — Semi-annual (180 days)' },
};

/** OS-INV-19 — Resolve null audit_class to 'C' for display (system default). */
const resolveAuditClass = (c: AuditClass | null | undefined): AuditClass =>
  c ?? 'C';

interface OsMaterial {
  id: string;
  name: string;
  type: string;
  unit: string;
  /** Total stock = stock_storeroom + stock_working. Kept in sync server-side. */
  currentStock: number;
  /** snake_case — not converted by formatMaterialFromDB (added post-OS-INV-11). */
  stock_storeroom: number;
  /** snake_case — not converted by formatMaterialFromDB (added post-OS-INV-11). */
  stock_working: number;
  minStock: number;
  reorderQuantity: number;
  active: boolean;
  supplier?: string;
  notes?: string;
  itemNumber?: number;
  color?: string;
  isMaster?: boolean;
  parentMaterialId?: string;
  /**
   * OS-INV-19 — Audit classification.
   * Staff-auth field — not present on PIN-session endpoints.
   */
  audit_class?: AuditClass | null;
}

interface NewMaterialForm {
  id: string;
  name: string;
  type: string;
  unit: string;
  initialStock: number;
  /** Location for initial stock. null = user must choose (no tenant default). */
  location: LocationKey | null;
  supplier: string;
  notes: string;
  minStock: number;
  reorderQuantity: number;
  active: boolean;
}

interface EditMaterialForm {
  id: string;
  name: string;
  type: string;
  unit: string;
  stock_storeroom: number;
  stock_working: number;
  minStock: number;
  reorderQuantity: number;
  active: boolean;
  supplier: string;
  notes: string;
  /** OS-INV-19: audit classification. Saved via PUT /api/os/materials/:id. */
  audit_class: AuditClass | null;
}

const LOCATION_LABELS: Record<LocationKey, string> = {
  storeroom: 'Store Room',
  working: 'Working Inventory',
};

const UNIT_OPTIONS = ['yards', 'pieces', 'feet', 'kg', 'sets', 'rolls', 'boxes'] as const;
const TYPE_OPTIONS = ['fabric', 'hardware', 'component', 'material'] as const;

// ── OsDefaultLocationPanel ─────────────────────────────────────────────────

interface OsDefaultLocationPanelProps {
  apiBase: string;
}

const OsDefaultLocationPanel: React.FC<OsDefaultLocationPanelProps> = ({ apiBase }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<DefaultLocation | undefined>(undefined); // undefined = loading
  const [selected, setSelected] = useState<LocationKey | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load current default
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${apiBase}/settings/default-location`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          const loc: DefaultLocation = data.defaultLocation ?? null;
          setCurrent(loc);
          setSelected(loc ?? '');
        }
      } catch {
        if (!cancelled) setCurrent(null); // treat error as "no default"
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  const handleSave = async (newLocation: LocationKey | null) => {
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch(`${apiBase}/settings/default-location`, {
        method: 'PUT',
        body: JSON.stringify({ defaultLocation: newLocation }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const saved: DefaultLocation = data.defaultLocation ?? null;
      setCurrent(saved);
      setSelected(saved ?? '');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const currentLabel = current === null
    ? 'Always Ask'
    : current === undefined
    ? '…'
    : LOCATION_LABELS[current];

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          bgcolor: open ? 'grey.100' : 'transparent',
          '&:hover': { bgcolor: 'grey.50' },
        }}
        onClick={() => setOpen(o => !o)}
        role="button"
        aria-expanded={open}
        aria-controls="default-location-panel"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontWeight={700} variant="subtitle1">
            Default Inventory Location
          </Typography>
          <Chip
            label={currentLabel}
            size="small"
            color={current ? 'primary' : 'default'}
            variant={current ? 'filled' : 'outlined'}
          />
          {success && <Chip label="Saved" size="small" color="success" />}
        </Stack>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={open} id="default-location-panel">
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            When adding inventory, pre-select this location. Set to{' '}
            <strong>Always Ask</strong> to require staff to choose every time.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Default Location</InputLabel>
              <Select
                value={selected}
                label="Default Location"
                onChange={e => setSelected(e.target.value as LocationKey | '')}
                disabled={saving}
              >
                <MenuItem value="working">Working Inventory</MenuItem>
                <MenuItem value="storeroom">Store Room</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              disabled={!selected || saving || selected === current}
              onClick={() => handleSave(selected as LocationKey)}
              sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' }, minWidth: 100 }}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Set Default
            </Button>

            <Button
              variant="outlined"
              size="small"
              color="inherit"
              disabled={current === null || saving}
              onClick={() => handleSave(null)}
            >
              Clear (Always Ask)
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── OsInventory ────────────────────────────────────────────────────────────

export const OsInventory: React.FC = () => {
  const navigate = useNavigate();
  const { apiBase, routeBase } = useTenant();

  const [materials, setMaterials]         = useState<OsMaterial[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [toast, setToast]                 = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [defaultLocation, setDefaultLocation] = useState<DefaultLocation | undefined>(undefined);

  // Dialog states
  const [addOpen, setAddOpen]             = useState(false);
  const [editOpen, setEditOpen]           = useState(false);
  // OS-INV-17: per-location item numbers (system-assigned, read-only display)
  const [itemNumStoreroom, setItemNumStoreroom] = useState<number | null>(null);
  const [itemNumWorking, setItemNumWorking]     = useState<number | null>(null);
  const [itemNumLoading, setItemNumLoading]     = useState(false);
  const [itemNumError, setItemNumError]         = useState('');
  const [assigningLoc, setAssigningLoc]         = useState<'storeroom' | 'working' | null>(null);
  const [deleteOpen, setDeleteOpen]       = useState(false);
  const [selectedMat, setSelectedMat]     = useState<OsMaterial | null>(null);

  // Form state
  const blankAdd = (): NewMaterialForm => ({
    id: '',
    name: '',
    type: 'hardware',
    unit: 'pieces',
    initialStock: 0,
    location: defaultLocation ?? null,
    supplier: '',
    notes: '',
    minStock: 0,
    reorderQuantity: 0,
    active: true,
  });
  const [addForm, setAddForm] = useState<NewMaterialForm>(blankAdd());
  const [editForm, setEditForm] = useState<EditMaterialForm | null>(null);
  const [saving, setSaving] = useState(false);
  // OS-INV-24: Reset Audit Schedule state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // ── Load default location ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${apiBase}/settings/default-location`);
        if (res.ok) {
          const data = await res.json();
          setDefaultLocation(data.defaultLocation ?? null);
        }
      } catch {
        setDefaultLocation(null);
      }
    })();
  }, [apiBase]);

  // ── Load materials ─────────────────────────────────────────────────────
  const loadMaterials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${apiBase}/materials`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows: OsMaterial[] = data.materials || data || [];
      setMaterials(rows.sort((a, b) => {
        if (a.itemNumber && b.itemNumber) return a.itemNumber - b.itemNumber;
        return a.name.localeCompare(b.name);
      }));
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  // ── Stock status helper ────────────────────────────────────────────────
  const stockStatus = (m: OsMaterial) => {
    const total = m.currentStock ?? (m.stock_storeroom + m.stock_working);
    if (total <= 0) return { color: 'error' as const, label: 'Out', icon: <WarningIcon /> };
    if (total < (m.minStock || 5)) return { color: 'warning' as const, label: 'Low', icon: <WarningIcon /> };
    return { color: 'success' as const, label: 'OK', icon: <CheckCircleIcon /> };
  };

  // ── Add dialog ─────────────────────────────────────────────────────────
  const openAdd = () => {
    setAddForm({ ...blankAdd(), location: defaultLocation ?? null });
    setAddOpen(true);
  };

  const handleAdd = async () => {
    if (!addForm.id || !addForm.name) {
      setToast({ msg: 'Material ID and Name are required', sev: 'error' });
      return;
    }
    if (addForm.initialStock > 0 && !addForm.location) {
      setToast({ msg: 'Please choose a location for the initial stock', sev: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${apiBase}/materials`, {
        method: 'POST',
        body: JSON.stringify({
          id: addForm.id.trim().toUpperCase(),
          name: addForm.name.trim(),
          type: addForm.type,
          unit: addForm.unit,
          currentStock: addForm.initialStock,
          location: addForm.location,        // OS-INV-16: location routing
          minStock: addForm.minStock,
          reorderQuantity: addForm.reorderQuantity,
          supplier: addForm.supplier || null,
          notes: addForm.notes || null,
          active: addForm.active,
          buyOrProduce: 'buy',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setToast({ msg: 'Material added successfully', sev: 'success' });
      setAddOpen(false);
      loadMaterials();
    } catch (e: unknown) {
      setToast({ msg: (e as Error).message ?? 'Failed to add material', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Edit dialog ────────────────────────────────────────────────────────
  const openEdit = (m: OsMaterial) => {
    setSelectedMat(m);
    setEditForm({
      id: m.id,
      name: m.name,
      type: m.type,
      unit: m.unit,
      stock_storeroom: m.stock_storeroom ?? 0,
      stock_working: m.stock_working ?? 0,
      minStock: m.minStock ?? 0,
      reorderQuantity: m.reorderQuantity ?? 0,
      active: m.active,
      supplier: m.supplier ?? '',
      notes: m.notes ?? '',
      audit_class: m.audit_class ?? null,  // OS-INV-19
    });
    setEditOpen(true);
    // OS-INV-17: load per-location item numbers (system-assigned, read-only)
    loadItemNumbers(m.id);
  };

  // OS-INV-17: load the system-assigned per-location item numbers for display.
  const loadItemNumbers = (materialId: string) => {
    setItemNumStoreroom(null);
    setItemNumWorking(null);
    setItemNumError('');
    setItemNumLoading(true);
    authFetch(`${apiBase}/admin/materials/${materialId}/item-numbers`)
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        for (const n of data.numbers ?? []) {
          if (n.location === 'storeroom') setItemNumStoreroom(n.itemNumber);
          if (n.location === 'working') setItemNumWorking(n.itemNumber);
        }
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => setItemNumLoading(false));
  };

  // OS-INV-17: trigger AUTO assignment for a location (no manual number).
  const assignItemNumber = async (location: 'storeroom' | 'working') => {
    if (!selectedMat) return;
    setItemNumError('');
    setAssigningLoc(location);
    try {
      const res = await authFetch(`${apiBase}/admin/materials/${selectedMat.id}/item-numbers/assign`, {
        method: 'POST',
        body: JSON.stringify({ location }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setItemNumError(data.detail || data.error || `Failed to assign ${location} item number`);
        return;
      }
      const data = await res.json();
      for (const n of data.numbers ?? []) {
        if (n.location === 'storeroom') setItemNumStoreroom(n.itemNumber);
        if (n.location === 'working') setItemNumWorking(n.itemNumber);
      }
    } finally {
      setAssigningLoc(null);
    }
  };

  const handleEdit = async () => {
    if (!editForm || !selectedMat) return;
    setSaving(true);
    try {
      const total = (editForm.stock_storeroom ?? 0) + (editForm.stock_working ?? 0);
      const res = await authFetch(`${apiBase}/materials/${selectedMat.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editForm.name.trim(),
          type: editForm.type,
          unit: editForm.unit,
          currentStock: total,                        // total for legacy compat
          stockStoreroom: editForm.stock_storeroom,   // OS-INV-16: Bridge will route these
          stockWorking: editForm.stock_working,
          minStock: editForm.minStock,
          reorderQuantity: editForm.reorderQuantity,
          active: editForm.active,
          supplier: editForm.supplier || null,
          notes: editForm.notes || null,
          audit_class: editForm.audit_class,          // OS-INV-19: A/B/C or null
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setToast({ msg: 'Material updated', sev: 'success' });
      setEditOpen(false);
      loadMaterials();
    } catch (e: unknown) {
      setToast({ msg: (e as Error).message ?? 'Failed to update', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Reset Audit Schedule — OS-INV-24 ────────────────────────────────────
  const handleResetAuditSchedule = async () => {
    if (!selectedMat) return;
    setResetting(true);
    try {
      const res = await authFetch(`${apiBase}/audit/schedule/${selectedMat.id}/reset`, {
        method: 'PUT',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setToast({ msg: `Audit schedule reset — ${selectedMat.name} will appear in the next count sheet.`, sev: 'success' });
      setResetDialogOpen(false);
    } catch (e: unknown) {
      setToast({ msg: `Failed to reset audit schedule: ${(e as Error).message ?? 'Unknown error'}`, sev: 'error' });
      setResetDialogOpen(false);
    } finally {
      setResetting(false);
    }
  };

  // ── Delete dialog ──────────────────────────────────────────────────────
  const openDelete = (m: OsMaterial) => {
    setSelectedMat(m);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedMat) return;
    setSaving(true);
    try {
      const res = await authFetch(`${apiBase}/materials/${selectedMat.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setToast({ msg: `"${selectedMat.name}" deleted`, sev: 'success' });
      setDeleteOpen(false);
      loadMaterials();
    } catch (e: unknown) {
      setToast({ msg: (e as Error).message ?? 'Failed to delete', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert onClose={() => setToast(null)} severity={toast?.sev ?? 'info'} sx={{ width: '100%' }}>
          {toast?.msg}
        </Alert>
      </Snackbar>

      {/* Default-location management panel */}
      <OsDefaultLocationPanel apiBase={apiBase} />

      {/* OsPinAdmin — existing PIN management (OS-INV-14) */}
      <OsPinAdmin />

      {/* Action bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={openAdd}
          >
            Add Item
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={() => window.open(`${routeBase}/count-sheet`, '_blank')}
          >
            Count Sheet
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => navigate(`${routeBase}/labels`)}
          >
            Labels
          </Button>
        </Stack>
      </Paper>

      {/* Error state */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Materials table */}
      {!loading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#001550' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Name / Code</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'right' }}>Store Room</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'right' }}>Working</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'right' }}>Total</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materials.filter(m => !m.isMaster).map((m, idx) => {
                const st = stockStatus(m);
                const storeroom = m.stock_storeroom ?? 0;
                const working = m.stock_working ?? 0;
                const total = m.currentStock ?? (storeroom + working);
                return (
                  <TableRow
                    key={m.id}
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'white' : 'grey.50',
                      opacity: m.active ? 1 : 0.5,
                    }}
                  >
                    <TableCell>
                      <Chip label={st.label} color={st.color} size="small" icon={st.icon} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{m.name}</Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {m.id}
                            {m.itemNumber ? ` · #${String(m.itemNumber).padStart(6, '0')}` : ''}
                          </Typography>
                        </Box>
                        {/* OS-INV-19: Audit class badge — staff-auth only (this
                            component is Firebase-protected; badge never appears in
                            PIN-session context). Null/unclassified renders as 'C'. */}
                        {(() => {
                          const cls = resolveAuditClass(m.audit_class);
                          const meta = AUDIT_CLASS_META[cls];
                          return (
                            <Tooltip title={meta.tooltip} placement="top">
                              <Box
                                sx={{
                                  minWidth: 20,
                                  height: 20,
                                  borderRadius: '4px',
                                  bgcolor: `${meta.color}18`,
                                  border: `1px solid ${meta.color}40`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  mt: 0.25,
                                  cursor: 'default',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    color: meta.color,
                                    lineHeight: 1,
                                    letterSpacing: '0.02em',
                                  }}
                                >
                                  {cls}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={storeroom <= 0 ? 'text.disabled' : 'text.primary'}
                        fontWeight={storeroom > 0 ? 600 : 400}
                      >
                        {storeroom}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={working <= 0 ? 'text.disabled' : 'text.primary'}
                        fontWeight={working > 0 ? 600 : 400}
                      >
                        {working}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700}>
                        {total}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{m.unit}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => openEdit(m)} aria-label={`Edit ${m.name}`}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => openDelete(m)} aria-label={`Delete ${m.name}`}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {materials.filter(m => !m.isMaster).length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No materials found. Add your first item above.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Add Material Dialog ─────────────────────────────────────────── */}
      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Add New Inventory Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* ID + Name */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Material ID *"
                value={addForm.id}
                onChange={e => setAddForm(f => ({ ...f, id: e.target.value.toUpperCase() }))}
                helperText="Unique code (e.g., HW-BOLT-M4)"
                fullWidth
                autoFocus
              />
              <TextField
                label="Name *"
                value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                fullWidth
              />
            </Stack>

            {/* Type + Unit */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={addForm.type} label="Type" onChange={e => setAddForm(f => ({ ...f, type: e.target.value }))}>
                  {TYPE_OPTIONS.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select value={addForm.unit} label="Unit" onChange={e => setAddForm(f => ({ ...f, unit: e.target.value }))}>
                  {UNIT_OPTIONS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            {/* Initial stock + Location */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Initial Stock
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={addForm.initialStock}
                  onChange={e => setAddForm(f => ({ ...f, initialStock: parseFloat(e.target.value) || 0 }))}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>{addForm.unit}</Typography> }}
                  fullWidth
                />
                <FormControl fullWidth error={addForm.initialStock > 0 && !addForm.location}>
                  <InputLabel>Location *</InputLabel>
                  <Select
                    value={addForm.location ?? ''}
                    label="Location *"
                    onChange={e => setAddForm(f => ({ ...f, location: (e.target.value as LocationKey) || null }))}
                  >
                    <MenuItem value="working">Working Inventory</MenuItem>
                    <MenuItem value="storeroom">Store Room</MenuItem>
                  </Select>
                  {defaultLocation === null && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      No tenant default — please choose
                    </Typography>
                  )}
                  {defaultLocation !== null && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Default: {defaultLocation ? LOCATION_LABELS[defaultLocation] : '—'}
                    </Typography>
                  )}
                </FormControl>
              </Stack>
            </Box>

            {/* Min stock + reorder qty */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Min Stock Level"
                type="number"
                value={addForm.minStock}
                onChange={e => setAddForm(f => ({ ...f, minStock: parseFloat(e.target.value) || 0 }))}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
              />
              <TextField
                label="Reorder Qty"
                type="number"
                value={addForm.reorderQuantity}
                onChange={e => setAddForm(f => ({ ...f, reorderQuantity: parseFloat(e.target.value) || 0 }))}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
              />
            </Stack>

            <TextField
              label="Supplier (optional)"
              value={addForm.supplier}
              onChange={e => setAddForm(f => ({ ...f, supplier: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Notes (optional)"
              value={addForm.notes}
              onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} color="inherit" disabled={saving}>Cancel</Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={saving || !addForm.id || !addForm.name || (addForm.initialStock > 0 && !addForm.location)}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
            sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
          >
            Add Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Material Dialog ────────────────────────────────────────── */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Edit: {selectedMat?.name}</DialogTitle>
        <DialogContent>
          {editForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Material ID" value={editForm.id} disabled fullWidth />
                <TextField
                  label="Name *"
                  value={editForm.name}
                  onChange={e => setEditForm(f => f ? { ...f, name: e.target.value } : f)}
                  fullWidth
                  autoFocus
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={editForm.type} label="Type" onChange={e => setEditForm(f => f ? { ...f, type: e.target.value } : f)}>
                    {TYPE_OPTIONS.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Unit</InputLabel>
                  <Select value={editForm.unit} label="Unit" onChange={e => setEditForm(f => f ? { ...f, unit: e.target.value } : f)}>
                    {UNIT_OPTIONS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>

              {/* Stock by location — OS-INV-16: show storeroom + working + total */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Stock by Location
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    label="Store Room"
                    type="number"
                    value={editForm.stock_storeroom}
                    onChange={e => setEditForm(f => f ? { ...f, stock_storeroom: parseFloat(e.target.value) || 0 } : f)}
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{ endAdornment: <Chip label="STOREROOM" size="small" color="success" sx={{ ml: 1, fontSize: '0.65rem' }} /> }}
                    fullWidth
                  />
                  <TextField
                    label="Working Inventory"
                    type="number"
                    value={editForm.stock_working}
                    onChange={e => setEditForm(f => f ? { ...f, stock_working: parseFloat(e.target.value) || 0 } : f)}
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{ endAdornment: <Chip label="WORKING" size="small" color="primary" sx={{ ml: 1, fontSize: '0.65rem' }} /> }}
                    fullWidth
                  />
                  <Box
                    sx={{
                      minWidth: 100,
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      textAlign: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {(editForm.stock_storeroom ?? 0) + (editForm.stock_working ?? 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{editForm.unit}</Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Total is read-only — sum of Store Room + Working.
                </Typography>
              </Box>

              {/* OS-INV-17: Item numbers per location — SYSTEM-ASSIGNED, read-only */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Item Numbers by Location
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Item numbers are assigned automatically by the system — one per
                  location. They cannot be edited. A number is assigned when a
                  location holds stock; use “Assign now” to create one early.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {([
                    { loc: 'storeroom' as const, label: 'Store Room', chip: 'STOREROOM', color: 'success' as const, value: itemNumStoreroom },
                    { loc: 'working' as const,   label: 'Working',   chip: 'WORKING',   color: 'primary' as const, value: itemNumWorking },
                  ]).map(({ loc, label, chip, color, value }) => (
                    <Box key={loc} sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Chip label={chip} size="small" color={color} sx={{ fontSize: '0.6rem' }} />
                      </Stack>
                      {itemNumLoading ? (
                        <Typography variant="body2" color="text.secondary">…</Typography>
                      ) : value != null ? (
                        <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'monospace' }}>
                          #{String(value).padStart(6, '0')}
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.disabled">Not assigned</Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={assigningLoc === loc}
                            onClick={() => assignItemNumber(loc)}
                          >
                            {assigningLoc === loc ? 'Assigning…' : 'Assign now'}
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
                {itemNumError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {itemNumError}
                  </Typography>
                )}
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Min Stock Level"
                  type="number"
                  value={editForm.minStock}
                  onChange={e => setEditForm(f => f ? { ...f, minStock: parseFloat(e.target.value) || 0 } : f)}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
                <TextField
                  label="Reorder Qty"
                  type="number"
                  value={editForm.reorderQuantity}
                  onChange={e => setEditForm(f => f ? { ...f, reorderQuantity: parseFloat(e.target.value) || 0 } : f)}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Supplier"
                value={editForm.supplier}
                onChange={e => setEditForm(f => f ? { ...f, supplier: e.target.value } : f)}
                fullWidth
              />
              <TextField
                label="Notes"
                value={editForm.notes}
                onChange={e => setEditForm(f => f ? { ...f, notes: e.target.value } : f)}
                multiline
                rows={2}
                fullWidth
              />

              {/* OS-INV-19: Audit Class selector — staff-auth only.
                  Controls how frequently this item is counted.
                  A = monthly (30d), B = quarterly (90d), C = semi-annual (180d).
                  NOT visible in any PIN-session context (scan page, count sheet). */}
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Audit Class
                  </Typography>
                  <Tooltip
                    title="Controls how frequently this item is counted. A = monthly, B = quarterly, C = semi-annually."
                    placement="right"
                  >
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        cursor: 'help',
                        borderBottom: '1px dashed',
                        borderColor: 'text.disabled',
                        lineHeight: 1,
                      }}
                    >
                      What is this?
                    </Typography>
                  </Tooltip>
                </Stack>
                <ToggleButtonGroup
                  value={editForm.audit_class}
                  exclusive
                  onChange={(_e, newClass: AuditClass | null) => {
                    // ToggleButtonGroup passes null when the same value is clicked
                    // again — treat as no change (keep current selection).
                    if (newClass !== null) {
                      setEditForm(f => f ? { ...f, audit_class: newClass } : f);
                    }
                  }}
                  size="small"
                  aria-label="Audit Class"
                  sx={{ flexWrap: 'wrap' }}
                >
                  {(['A', 'B', 'C'] as AuditClass[]).map((cls) => (
                    <ToggleButton
                      key={cls}
                      value={cls}
                      aria-label={AUDIT_CLASS_META[cls].tooltip}
                      sx={{
                        px: 2,
                        textTransform: 'none',
                        '&.Mui-selected': {
                          bgcolor: `${AUDIT_CLASS_META[cls].color}18`,
                          borderColor: `${AUDIT_CLASS_META[cls].color}80`,
                          color: AUDIT_CLASS_META[cls].color,
                          fontWeight: 700,
                        },
                      }}
                    >
                      {cls === 'A' && 'A — Monthly (30 days)'}
                      {cls === 'B' && 'B — Quarterly (90 days)'}
                      {cls === 'C' && 'C — Semi-annual (180 days)'}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Unclassified items default to Class C (semi-annual counting).
                </Typography>
              </Box>

              {/* ── Audit Schedule Reset — OS-INV-24 ──────────────────────────── */}
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Audit Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Force this item to appear due in the next count cycle. Useful when you need
                  an accurate count immediately.
                </Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => setResetDialogOpen(true)}
                >
                  Reset Audit Schedule
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit" disabled={saving}>Cancel</Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={saving || !editForm?.name}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
            sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Audit Schedule Confirmation Dialog — OS-INV-24 ────────── */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !resetting && setResetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>Reset Audit Schedule?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <strong>{selectedMat?.name}</strong> will appear as due in the next count
            cycle. The scheduled date will be set to today.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This does not affect stock counts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setResetDialogOpen(false)}
            color="inherit"
            disabled={resetting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetAuditSchedule}
            variant="contained"
            color="warning"
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={16} /> : <RefreshIcon />}
          >
            Reset Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ───────────────────────────────────────── */}
      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Delete Material?</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{selectedMat?.name}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} color="inherit" disabled={saving}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
